import { Routes, Route, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

import Home from '@/pages/public/Home';
import Category from '@/pages/public/Category';
import ProductDetail from '@/pages/public/ProductDetail';
import Checkout from '@/pages/public/Checkout';
import Cart from '@/pages/public/Cart';
import Saved from '@/pages/public/Saved';
import Bidding from '@/pages/public/Bidding';
import AuctionDetail from '@/pages/public/AuctionDetail';
import FreeAndDonations from '@/pages/public/FreeAndDonations';
import Trust from '@/pages/public/Trust';
import Membership from '@/pages/public/Membership';
import AiListing from '@/pages/public/AiListing';
import About from '@/pages/public/About';
import Help from '@/pages/public/Help';
import { Terms, Cookies, Contact } from '@/pages/public/Legal';
import { PrivacyPolicy } from '@/pages/public/PrivacyPolicy';
import { BuyerProtection, DisputeResolution, StolenGoods } from '@/pages/public/Safety';
import { RefundPolicy } from '@/pages/public/RefundPolicy';
import TrackParcel from '@/pages/public/TrackParcel';
import Sellers from '@/pages/public/Sellers';
import SellerWardrobe from '@/pages/public/SellerWardrobe';
import DashboardMembership from '@/pages/dashboard/Membership';
import Grow from '@/pages/dashboard/Grow';

import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import Forgot from '@/pages/auth/Forgot';
import ResetPassword from '@/pages/auth/ResetPassword';

import DashboardOverview from '@/pages/dashboard/Overview';
import Orders from '@/pages/dashboard/Orders';
import Sales from '@/pages/dashboard/Sales';
import Offers from '@/pages/dashboard/Offers';
import { WalletRouter } from '@/pages/dashboard/WalletRouter';
import SellNew from '@/pages/dashboard/SellNew';
import SwapDevice from '@/pages/dashboard/SwapDevice';
import DonatePost from '@/pages/dashboard/DonatePost';
import Returns from '@/pages/dashboard/Returns';
import Profile from '@/pages/dashboard/Profile';
import Listed from '@/pages/dashboard/Listed';
import MyListings from '@/pages/dashboard/MyListings';
import SellerReturns from '@/pages/dashboard/SellerReturns';

import AdminOverview from '@/pages/admin/Overview';
import VerificationQueue from '@/pages/admin/VerificationQueue';
import EscrowPayouts from '@/pages/admin/EscrowPayouts';
import Complaints from '@/pages/admin/Complaints';
import AdminUsers from '@/pages/admin/Users';
import AdminUserDetailPage from '@/pages/admin/AdminUserDetailPage';
import Appeals from '@/pages/admin/Appeals';
import AdminListings from '@/pages/admin/Listings';
import AdminOrders from '@/pages/admin/Orders';
import AdminOrderDetailRoute from '@/pages/admin/AdminOrderDetailRoute';

import NotFound from '@/pages/NotFound';
import { DemoBanner } from '@/components/DemoBanner';
import { ScrollToTop } from '@/components/ScrollToTop';

export default function App() {
  return (
    <>
    <ScrollToTop />
    <DemoBanner />
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="/c/:slug" element={<Category />} />
        <Route path="/p/:id" element={<ProductDetail />} />
        <Route path="/checkout/:orderId" element={<Checkout />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/saved" element={<Saved />} />

        {/* Bidding retired → redirect shims */}
        <Route path="/bid" element={<Bidding />} />
        <Route path="/bid/:id" element={<AuctionDetail />} />

        <Route path="/free" element={<FreeAndDonations />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/ai-listing" element={<AiListing />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/buyer-protection" element={<BuyerProtection />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/refunds" element={<RefundPolicy />} />
        <Route path="/disputes" element={<DisputeResolution />} />
        <Route path="/stolen-goods" element={<StolenGoods />} />
        <Route path="/help" element={<Help />} />
        <Route path="/track/:code" element={<TrackParcel />} />
        <Route path="/sellers" element={<Sellers />} />
        <Route path="/seller/:handle" element={<SellerWardrobe />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/forgot-password" element={<Forgot />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>

      {/* Standalone email-verification step: register lands here, and on
          success the user is moved into the dashboard. */}
      <Route
        path="/verify-email"
        element={
          <ProtectedRoute>
            <VerifyEmail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverview />} />
        <Route path="orders" element={<Orders />} />
        <Route path="sales" element={<Sales />} />
        <Route path="listings" element={<MyListings />} />
        <Route path="seller-returns" element={<SellerReturns />} />
        <Route path="offers" element={<Offers />} />
        <Route path="wallet" element={<WalletRouter />} />
        <Route path="sell" element={<SellNew />} />
        <Route path="listed" element={<Listed />} />
        <Route path="swap" element={<SwapDevice />} />
        <Route path="donate" element={<DonatePost />} />
        <Route path="returns" element={<Returns />} />
        <Route path="profile" element={<Profile />} />
        <Route path="membership" element={<DashboardMembership />} />
        <Route path="grow" element={<Grow />} />
      </Route>

      <Route
        path="/control-7f3a9b2c"
        element={
          <ProtectedRoute role="ADMIN">
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="verify" element={<VerificationQueue />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetailPage />} />
        <Route path="appeals" element={<Appeals />} />
        <Route path="listings" element={<AdminListings />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="orders/:id" element={<AdminOrderDetailRoute />} />
        <Route path="escrow" element={<EscrowPayouts />} />
        <Route path="complaints" element={<Complaints />} />
      </Route>

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
    </>
  );
}
